import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Award, Download, Eye, ArrowLeft, ShieldCheck, XCircle, Clock, Ban,
  RefreshCw, Copy, ExternalLink, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// =========================================================================
// HOSPITAL ADMIN - Cert dashboard + register
// =========================================================================
export function OrgCertDashboardPage() {
  const [totals, setTotals] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/org/certificate-register");
        setTotals(data.data.totals);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!totals) return <DashboardLayout title="Certificates"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Certificate Register" breadcrumbs={["Hospital Admin", "Certificates"]}>
      <div className="space-y-6" data-testid="org-cert-dashboard">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard label="Total" value={totals.total} icon="Award" tone="navy" testId="org-cert-total" index={0} />
          <StatCard label="Valid" value={totals.valid} icon="ShieldCheck" tone="green" testId="org-cert-valid" index={1} />
          <StatCard label="Expired" value={totals.expired} icon="Clock" tone="amber" testId="org-cert-expired" index={2} />
          <StatCard label="Revoked" value={totals.revoked} icon="Ban" tone="rose" testId="org-cert-revoked" index={3} />
          <StatCard label="Renewal due" value={totals.renewal_due} icon="RefreshCw" tone="slate" testId="org-cert-renewal" index={4} />
        </section>
        <Link to="/app/hospital/certificate-register" className="inline-block rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="org-cert-open-register">
          Open register →
        </Link>
      </div>
    </DashboardLayout>
  );
}

export function OrgCertRegisterPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: "all", search: "" });
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const { data } = await api.get("/org/certificates", { params });
      setRows(data.data.certificates);
      setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);
  const download = (cid) => window.open(`${BACKEND}/api/org/certificates/${cid}/download`, "_blank");
  return (
    <DashboardLayout title="Staff Certificates" breadcrumbs={["Hospital Admin", "Register"]}>
      <div className="space-y-4" data-testid="org-register">
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No certificates" description="Certificates for your staff will appear here." icon={Award} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Cert #</th><th className="px-4 py-3 text-left">Learner</th><th className="px-4 py-3 text-left">Course</th><th className="px-4 py-3 text-left">Issued</th><th className="px-4 py-3 text-left">Expires</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Action</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`org-cert-row-${c.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{c.certificate_number}</td>
                    <td className="px-4 py-2">{c.learner?.full_name}<div className="text-xs text-slate-500">{c.learner?.email}</div></td>
                    <td className="px-4 py-2 text-xs">{c.course?.course_title}</td>
                    <td className="px-4 py-2 text-xs">{c.issue_date?.slice(0, 10)}</td>
                    <td className="px-4 py-2 text-xs">{c.expiry_date?.slice(0, 10) || "—"}</td>
                    <td className="px-4 py-2"><StatusBadge status={c.computed_status || c.certificate_status} /></td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => download(c.id)} className="text-xs font-bold text-[#101876] hover:underline" data-testid={`org-cert-download-${c.id}`}>Download</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// LEARNER - My certificates
// =========================================================================
export function LearnerCertsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/learner/certificates");
        setRows(data.data.certificates);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  const download = (cid) => window.open(`${BACKEND}/api/learner/certificates/${cid}/download`, "_blank");

  return (
    <DashboardLayout title="My Certificates" breadcrumbs={["My Learning", "Certificates"]}>
      <div className="space-y-4" data-testid="learner-certs">
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No certificates yet" description="Complete a course + pass its exam to earn your first certificate." icon={Award} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((c) => (
              <div key={c.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-md transition" data-testid={`learner-cert-${c.id}`}>
                <div className="h-28 bg-gradient-to-br from-[#101876] to-[#329112] p-4 text-white">
                  <Award className="h-6 w-6 opacity-90" />
                  <div className="text-sm font-bold mt-2 line-clamp-1">{c.course?.course_title}</div>
                  <div className="text-xs opacity-80 font-mono mt-1">{c.certificate_number}</div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <StatusBadge status={c.computed_status || c.certificate_status} />
                    <span className="text-slate-500">Issued {c.issue_date?.slice(0, 10)}</span>
                  </div>
                  {c.percentage != null && <div className="text-xs text-slate-500">Score: <b>{c.percentage}%</b></div>}
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => download(c.id)} disabled={c.certificate_status === "revoked"}
                      className="flex-1 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c] disabled:opacity-40 flex items-center justify-center gap-1" data-testid={`learner-cert-download-${c.id}`}>
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                    <Link to={`/verify-certificate/${c.verification_code}`} target="_blank"
                      className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1" data-testid={`learner-cert-verify-${c.id}`}>
                      <ShieldCheck className="h-3.5 w-3.5" /> Verify
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// PUBLIC VERIFY PAGE (no auth)
// =========================================================================
export function PublicVerifyPage() {
  const { code } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/verify-certificate/${code}`);
        setResult(data.data);
      } catch {
        setResult({ valid: false, status: "invalid" });
      }
      finally { setLoading(false); }
    })();
  }, [code]);

  if (loading) return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center">
      <div className="text-slate-500 text-sm">Verifying certificate…</div>
    </div>
  );

  const s = result?.status || "invalid";
  const bg = {
    valid: "from-emerald-500 to-emerald-700",
    expiring_soon: "from-emerald-500 to-emerald-700",
    expired: "from-amber-500 to-amber-700",
    revoked: "from-rose-500 to-rose-700",
    reissued: "from-slate-500 to-slate-700",
    invalid: "from-rose-500 to-rose-700",
  }[s] || "from-slate-500 to-slate-700";
  const label = {
    valid: "Valid Certificate", expiring_soon: "Valid (Expiring Soon)",
    expired: "Certificate Expired", revoked: "Certificate Revoked",
    reissued: "Certificate Reissued", invalid: "Invalid Certificate",
  }[s] || s;
  const Icon = ({ valid: CheckCircle2, expiring_soon: AlertTriangle, expired: Clock, revoked: Ban, invalid: XCircle }[s] || XCircle);
  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4" data-testid="public-verify">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xl">
          <div className={`bg-gradient-to-br ${bg} p-8 text-white text-center`}>
            <Icon className="h-14 w-14 mx-auto mb-3 opacity-95" />
            <div className="text-2xl font-bold" data-testid="verify-status-label">{label}</div>
            <div className="text-xs opacity-80 mt-2">Verification Code: <span className="font-mono">{code}</span></div>
          </div>
          {result?.valid || ["expired", "revoked", "reissued"].includes(s) ? (
            <div className="p-6 space-y-4">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Issued to</div>
                <div className="text-lg font-bold mt-1" data-testid="verify-learner">{result.learner_name}</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 text-sm">
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Course</div>
                  <div className="mt-1">{result.course_title}</div>
                  {result.course_code && <div className="font-mono text-xs text-slate-500">{result.course_code}</div>}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Certificate #</div>
                  <div className="mt-1 font-mono">{result.certificate_number}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Issue date</div>
                  <div className="mt-1">{result.issue_date?.slice(0, 10)}</div>
                </div>
                {result.organization_name && (
                  <div className="sm:col-span-2">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Organization</div>
                    <div className="mt-1">{result.organization_name}</div>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Issued by</div>
                  <div className="mt-1">{result.issued_by}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-slate-600">
              This certificate could not be verified. If you believe this is an error, please contact SIHMT support.
            </div>
          )}
          <div className="border-t border-slate-100 p-4 text-center text-xs text-slate-500">
            Verified via Steward Learning Cloud · Public Certificate Registry
          </div>
        </div>
        <div className="text-center mt-6 text-xs text-slate-500">
          © Steward Healthcare India Pvt. Ltd. · SIHMT
        </div>
      </div>
    </div>
  );
}
